import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { emailTemplateService, EmailTemplate } from '../../services/emailTemplateService';

const EmailTemplateTest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [language, setLanguage] = useState<'en' | 'ar'>('en');
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadTemplate(parseInt(id));
    }
  }, [id]);

  const loadTemplate = async (templateId: number) => {
    try {
      setLoading(true);
      setError(null);

      const data = await emailTemplateService.getTemplate(templateId);

      if (!data) {
        throw new Error('No template data received');
      }

      setTemplate(data);

      // Initialize test variables with placeholder values
      const variables = data.variables || [];
      const initialVariables: Record<string, string> = {};
      variables.forEach(variable => {
        initialVariables[variable] = getDefaultValue(variable);
      });
      setTestVariables(initialVariables);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultValue = (variable: string): string => {
    const defaults: Record<string, string> = {
      user_name: 'John Doe',
      request_number: 'REQ-TEST-001',
      company_name: 'Test Company Ltd.',
      package_name: 'Standard Package',
      amount: '1000.00',
      currency: 'USD',
      verification_code: '123456',
      reset_code: '12345',
      ticket_id: 'TKT-001',
      message_subject: 'Test Message',
      invoice_number: 'INV-001',
      payment_date: new Date().toLocaleDateString(),
      failure_reason: 'Insufficient funds',
      payment_link: 'https://patrixglobal.com/payment/retry'
    };
    return defaults[variable] || `[TEST_${variable.toUpperCase()}]`;
  };

  const handleSendTest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;

    try {
      setSending(true);
      setError(null);
      setSuccess(null);

      await emailTemplateService.testEmail({
        template_id: template.id,
        test_email: testEmail,
        language,
        test_variables: testVariables
      });

      setSuccess(`Test email sent successfully to ${testEmail}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const previewSubject = () => {
    if (!template) return '';
    const subject = language === 'ar' && template.subject_ar ? template.subject_ar : template.subject;
    return replaceVariables(subject);
  };

  const previewContent = (isHtml: boolean = false) => {
    if (!template) return '';
    let content = '';

    if (language === 'ar') {
      content = isHtml ? (template.body_html_ar || template.body_html) : (template.body_text_ar || template.body_text);
    } else {
      content = isHtml ? template.body_html : template.body_text;
    }

    return replaceVariables(content);
  };

  const replaceVariables = (content: string) => {
    let result = content;
    Object.entries(testVariables).forEach(([key, value]) => {
      const placeholder = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(placeholder, value);
    });
    return result;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!template) {
    return (
      <AdminLayout>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-6xl mb-4">📧</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Template Not Found</h3>
          <p className="text-gray-600 mb-6">The requested email template could not be found.</p>
          <button
            onClick={() => navigate('/admin/email-templates')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            ← Back to Templates
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="w-full max-w-none space-y-8">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg">
                <span className="text-2xl">📧</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Test Email Template</h1>
                <p className="text-gray-600 mt-1">
                  Template: <span className="font-medium text-primary-600">{template.template_name}</span>
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/email-templates')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <span>←</span>
              <span>Back to Templates</span>
            </button>
          </div>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-red-500 mr-3 mt-0.5">⚠️</div>
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-green-500 mr-3 mt-0.5">✅</div>
              <div>
                <h3 className="text-sm font-medium text-green-800">Success</h3>
                <p className="text-sm text-green-700 mt-1">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-8">
          {/* Left Column - Test Configuration */}
          <div className="w-full space-y-6">
            {/* Test Email Form */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-primary-600">⚙️</div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Test Configuration</h2>
                    <p className="text-sm text-gray-600">Configure email test settings</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <form onSubmit={handleSendTest} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Test Email Address
                    </label>
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="Enter email address to receive test"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Language
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setLanguage('en')}
                        className={`px-4 py-3 rounded-lg border transition-all ${
                          language === 'en'
                            ? 'bg-primary-50 border-primary-200 text-primary-700 font-medium'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        🇺🇸 English
                      </button>
                      <button
                        type="button"
                        onClick={() => setLanguage('ar')}
                        className={`px-4 py-3 rounded-lg border transition-all ${
                          language === 'ar'
                            ? 'bg-primary-50 border-primary-200 text-primary-700 font-medium'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        🇸🇦 العربية
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={sending || !testEmail}
                    className="w-full px-6 py-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-colors"
                  >
                    {sending ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Sending Test Email...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">📤</span>
                        Send Test Email
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Template Variables */}
            {template.variables && template.variables.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="text-primary-600">🔧</div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Template Variables</h2>
                      <p className="text-sm text-gray-600">Customize test values for placeholders</p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {template.variables.map((variable) => (
                      <div key={variable} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                            {`{{${variable}}}`}
                          </span>
                          {variable.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </label>
                        <input
                          type="text"
                          value={testVariables[variable] || ''}
                          onChange={(e) => setTestVariables({
                            ...testVariables,
                            [variable]: e.target.value
                          })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder={`Enter test value for ${variable}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Email Preview */}
          <div className="w-full space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-primary-600">👁️</div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Email Preview</h2>
                    <p className="text-sm text-gray-600">Preview how the email will appear to recipients</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Subject Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Subject Line
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-900">
                      {previewSubject() || 'No subject available'}
                    </div>
                  </div>
                </div>

                {/* Email Content Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Email Content
                  </label>
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <iframe
                      className="w-full min-h-[500px] max-h-[600px] border-0"
                      style={{
                        fontSize: '14px',
                        lineHeight: '1.6'
                      }}
                      srcDoc={`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <meta charset="UTF-8">
                          <meta name="viewport" content="width=device-width, initial-scale=1.0">
                          <style>
                            body {
                              margin: 0;
                              padding: 24px;
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                              font-size: 14px;
                              line-height: 1.6;
                              color: #333;
                              background: #ffffff;
                              direction: ${language === 'ar' ? 'rtl' : 'ltr'};
                            }
                            * {
                              box-sizing: border-box;
                            }
                            .no-content {
                              text-align: center;
                              color: #6b7280;
                              font-style: italic;
                              padding: 48px 0;
                            }
                          </style>
                        </head>
                        <body>
                          ${previewContent(true) || '<div class="no-content">No email content available for preview</div>'}
                        </body>
                        </html>
                      `}
                      title="Email Preview"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmailTemplateTest;